/*
 * ============================================================================
 * DATOS FICTICIOS — escenario de pruebas. Los agentes, departamentos,
 * usuarios y tenants de este simulador son inventados y NO describen el
 * producto ni lo desplegado. Solo se usa en desarrollo y en las pruebas del
 * Pórtico; en producción está apagado.
 * ============================================================================
 */

/**
 * Mock del visor de mecanismo (GEN-76 T7).
 *
 * Un mock que dice reproducir la maquinaria del kernel debe reproducir también
 * sus VALIDACIONES y sus CÓDIGOS DE ERROR. Es la lección de #493/#494: el
 * borrado de conversaciones pasó CI en verde y falló en el primer intento en
 * producción porque el mock aceptaba una propuesta que el kernel rechaza. Un
 * mock sin las validaciones del original no es una prueba: es una coartada.
 *
 * Se replican aquí, con el mismo `code` del envelope que emite el kernel:
 *   - mecanismo inexistente          → 404 `mecanismo_no_encontrado`
 *   - filtro no declarado            → 422 `filtro_no_declarado`
 *   - valor fuera del enumerado      → 422 `valor_no_declarado`
 *   - acción no declarada            → 422 `accion_no_declarada`
 *   - campo no declarado / de solo lectura → 422 `campo_no_declarado`
 *   - campo no editable en el estado actual → 422 `campo_no_editable_en_estado`
 *   - alta sin campos requeridos     → 422 `parametros_invalidos`
 *   - transición sin motivo          → 422 `parametros_invalidos`
 *
 * Y la paridad de DOCUMENTO: el descriptor que sirve este mock cumple lo que el
 * kernel valida al arrancar, porque un mock que sirviera un descriptor que el
 * kernel rechaza pintaría en verde una pantalla que en producción no existe.
 *
 * Lo que este mock NO reproduce, y se dice en vez de suponerlo: la ejecución.
 * `proponerAccionMock` deja la acción en `pendiente_aprobacion` y aquí no hay
 * escritura en ningún almacén de mecanismo, así que la revalidación de V-5a —la
 * lista blanca aplicada OTRA VEZ en el momento de escribir— no tiene superficie
 * que replicar. Cuando el mock ejecute, la tendrá.
 *
 * El mecanismo del mock es SINTÉTICO (`MEC-DEMO`): el mock del genérico tampoco
 * puede saber qué mecanismos existen de verdad.
 */

import { ApiError } from '../api/errors.js'

const CODIGO = 'MEC-DEMO'

export const ESQUEMA_DEMO = {
  descriptor_version: 1,
  mecanismo: {
    codigo: CODIGO,
    nombre: 'Mecanismo de demostración',
    icono: 'mecanismo',
    custodio_binding: 'MECH_STEWARD',
    store_binding: 'MECH_STORE',
  },
  objeto: {
    etiqueta_singular: 'ficha',
    etiqueta_plural: 'fichas',
    clave: 'codigo',
    titulo_campo: 'enunciado',
    subtitulo_campo: null,
  },
  clasificacion: [
    { id: 'ambito', etiqueta: 'Ámbito', valores: ['plataforma', 'tenant'], requiere_ref: false, fuente_ref: 'ninguna' },
    { id: 'severidad', etiqueta: 'Severidad', valores: ['alta', 'baja'], requiere_ref: false, fuente_ref: 'ninguna' },
  ],
  ciclo_de_vida: {
    estados: ['propuesto', 'vigente', 'retirado'],
    inicial: 'propuesto',
    vigente: 'vigente',
    terminales: ['retirado'],
    transiciones: [
      { de: 'propuesto', a: 'vigente', etiqueta: 'Aprobar', requiere_aprobacion_owner: true, requiere_motivo: true, validaciones: [] },
      // La retirada exige la MISMA puerta que la entrada en vigor: sin ella, el
      // proponente aprobaría su propia retirada y una regla viva desaparecería
      // del tenant sin que el Owner viera nada. El kernel rechaza al arrancar
      // cualquier descriptor que declare esta transición sin puerta, así que el
      // mock tampoco puede servir uno que el kernel no aceptaría.
      { de: 'vigente', a: 'retirado', etiqueta: 'Retirar', requiere_aprobacion_owner: true, requiere_motivo: true, validaciones: [] },
    ],
  },
  versionado: { modelo: 'fila_mutable_con_snapshots', campo_version: 'version' },
  campos: [
    { id: 'codigo', etiqueta: 'Código', tipo: 'texto_corto', requerido: true, editable_en: ['propuesto'], ayuda: '', opciones: null, opciones_binding: null, widget: null, solo_lectura: false },
    // D-11 — la guarda provisional dejó de ser prosa y es ahora la salida (b) del
    // validador. Este descriptor elige la salida (a): el contenido en vigor SE
    // PUEDE reescribir, y por eso la acción de edición declara la puerta humana.
    // Con la (b) —lo que este campo declaraba antes— la lista blanca rechazaría al
    // proponer y la puerta NUNCA se evaluaría, así que el mock no podría ejercer
    // el mecanismo nuevo: sería paridad de documento sin paridad de comportamiento,
    // que es media paridad. Los otros campos se quedan en `['propuesto']`, así que
    // `campo_no_editable_en_estado` sigue siendo ejercitable por ellos.
    { id: 'enunciado', etiqueta: 'Enunciado', tipo: 'texto_corto', requerido: true, editable_en: ['propuesto', 'vigente'], ayuda: '', opciones: null, opciones_binding: null, widget: null, solo_lectura: false },
    // `editable_en` declara en qué estados se puede escribir este campo. Lo honra
    // el visor (no lo pinta fuera de ellos) Y el kernel (lo rechaza con
    // `campo_no_editable_en_estado`): antes solo lo miraba el front, y entonces
    // bastaba un cuerpo construido a mano para reescribir el texto de un objeto
    // en vigor sin pasar por ninguna puerta.
    { id: 'detalle', etiqueta: 'Detalle', tipo: 'texto_largo', requerido: false, editable_en: ['propuesto'], ayuda: '', opciones: null, opciones_binding: null, widget: null, solo_lectura: false },
    { id: 'severidad', etiqueta: 'Severidad', tipo: 'enum', requerido: false, editable_en: ['propuesto'], ayuda: '', opciones: ['alta', 'baja'], opciones_binding: null, widget: null, solo_lectura: false },
    // V-5b — `editable_en` va a NULL, no a `[]`. La lista vacía se lee «en ningún
    // estado» y hasta V-5b significaba «en todos»; el kernel ya NO arranca con un
    // descriptor que la declare, así que el mock tampoco puede servir uno con
    // ella (paridad de DOCUMENTO). Este campo no necesita restricción de estado:
    // `solo_lectura` ya lo cierra en absoluto, que es más fuerte.
    { id: 'version', etiqueta: 'Versión', tipo: 'texto_corto', requerido: false, editable_en: null, ayuda: '', opciones: null, opciones_binding: null, widget: null, solo_lectura: true },
  ],
  proyecciones: { objetos: 'v_demo_objetos', objeto_detalle: 'v_demo_detalle', versiones: 'v_demo_versiones', metricas: 'v_demo_metricas' },
  listado: {
    columnas: ['codigo', 'enunciado', 'ambito', 'estado'],
    // `estado` va el PRIMERO, y el orden lo decide cada descriptor: la cola de
    // aprobación es lo que un custodio abre esta pantalla a mirar (GEN-76·133).
    filtros: ['estado', 'ambito', 'severidad'],
    busqueda: ['enunciado', 'detalle'],
    orden_default: 'codigo',
    orden_sentido: 'asc',
  },
  detalle: { secciones: [{ id: 'cuerpo', titulo: 'Cuerpo', campos: ['enunciado', 'detalle', 'severidad'], widget: null }] },
  registros_ligados: [
    { id: 'auditoria', etiqueta: 'Auditoría', proyeccion: 'v_demo_auditoria', columnas: ['resultado', 'evidencia'], alta: 'registrar_auditoria' },
  ],
  metricas: { tarjetas: [{ id: 'total', etiqueta: 'Fichas', metrica_id: 'n_fichas' }] },
  paneles_extra: [],
  acciones: [
    { id: 'alta', etiqueta: 'Nueva ficha', tipo: 'alta', accion_kernel: 'mec_proponer_objeto', scope_requerido: `mec:${CODIGO}:proponer`, transicion: null, registro_ligado: null },
    // La edición existe para que la regla de `editable_en` sea EJERCITABLE y no
    // una rama muerta del mock: sobre `detalle` o `severidad` de una ficha en vigor
    // el kernel responde `campo_no_editable_en_estado`.
    // D-11 — y declara la puerta humana (salida (a)): con el modelo de fila mutable
    // la fila ES lo que está en vigor, así que reescribir el contenido de una ficha
    // vigente exige una segunda persona. El validador del kernel NO arranca con una
    // acción de edición sin esta clave si algún campo escribible es editable en
    // vigor, de modo que el mock tampoco puede servir un descriptor sin ella.
    { id: 'editar', etiqueta: 'Editar', tipo: 'edicion', accion_kernel: 'mec_proponer_objeto', scope_requerido: `mec:${CODIGO}:proponer`, transicion: null, registro_ligado: null, requiere_aprobacion_owner: true },
    { id: 'aprobar', etiqueta: 'Aprobar', tipo: 'transicion', accion_kernel: 'mec_transicionar_objeto', scope_requerido: `mec:${CODIGO}:proponer`, transicion: 'propuesto->vigente', registro_ligado: null },
    { id: 'registrar_auditoria', etiqueta: 'Registrar auditoría', tipo: 'registro', accion_kernel: 'mec_registrar_traza', scope_requerido: `mec:${CODIGO}:proponer`, transicion: null, registro_ligado: 'auditoria' },
  ],
  modo_consulta: { visible_para: 'universo_cerrado', catalogo_completo: true },
}

const OBJETOS = [
  { codigo: 'DEMO-01', enunciado: 'Primera ficha', detalle: 'Cuerpo de la primera.', ambito: 'tenant', severidad: 'alta', estado: 'vigente', version: '1.0' },
  { codigo: 'DEMO-02', enunciado: 'Segunda ficha', detalle: 'Cuerpo de la segunda.', ambito: 'plataforma', severidad: 'baja', estado: 'propuesto', version: '0.1' },
]

// R-18 (GEN-76·135) — el `aprobador` viaja en cada fila del historial, y el mock
// trae LAS DOS FORMAS a propósito: una con valor y otra a `null`. El hueco es
// tan parte del contrato como el valor —la proyección lo sirve sin `coalesce`
// para que se vea—, así que una fixture que solo trajera filas rellenas dejaría
// el camino del hueco sin ejercitar, que es el que se rompe en silencio.
const VERSIONES = {
  'DEMO-01': [
    { codigo: 'DEMO-01', version: '1.1', es_vigente: true, autor: 'custodio', aprobador: 'owner', fecha: '2026-07-21', motivo: 'puesta en vigor' },
    { codigo: 'DEMO-01', version: '1.0', es_vigente: false, autor: 'custodio', aprobador: null, fecha: '2026-07-20', motivo: 'alta' },
  ],
}

const AUDITORIA = {
  'DEMO-01': [{ codigo: 'DEMO-01', registro_id: 'a1', resultado: 'conforme', evidencia: 'acta-1' }],
}

function esquemaDe(codigo) {
  if (codigo !== CODIGO) {
    throw new ApiError('mecanismo_no_encontrado', 'mecanismo no encontrado', 404)
  }
  return ESQUEMA_DEMO
}

export function listMecanismosMock() {
  return {
    mecanismos: [
      {
        codigo: CODIGO,
        nombre: ESQUEMA_DEMO.mecanismo.nombre,
        icono: 'mecanismo',
        etiqueta_plural: 'fichas',
        capacidades: { entrar: true, proponer: true, escritura_disponible: true, versiones: true, metricas: true },
        acciones: ESQUEMA_DEMO.acciones.map(a => a.id),
      },
    ],
  }
}

export function getEsquemaMock(codigo) {
  return esquemaDe(codigo)
}

/**
 * Enumerado CERRADO de un filtro declarado, o `null` si ese nombre no es
 * filtrable. Es la copia en JavaScript de `DescriptorMecanismo.valores_de_filtro`
 * del kernel (GEN-76·133, decisión (b) de Werner): `estado` sale del CICLO DE
 * VIDA y el resto de `clasificacion`.
 *
 * Que exista una copia no es descuido: kernel y mock son dos ejecuciones y la
 * paridad se sostiene con tests de contrato, no con un fichero compartido. Lo
 * que sí se evita es la copia DENTRO de cada ejecución — por eso el visor
 * consume esta misma función y no vuelve a resolver por su cuenta.
 */
export function valoresDeFiltro(esquema, eje) {
  if (eje === 'estado') return esquema.ciclo_de_vida?.estados ?? null
  const declarado = (esquema.clasificacion ?? []).find(e => e.id === eje)
  return declarado ? declarado.valores : null
}

export function listObjetosMock(codigo, { q, filtros, pagina = 1, tam = 25 } = {}) {
  const d = esquemaDe(codigo)
  for (const [eje, valor] of Object.entries(filtros ?? {})) {
    if (!d.listado.filtros.includes(eje)) {
      throw new ApiError('filtro_no_declarado', `'${eje}' no es un filtro declarado`, 422)
    }
    const valores = valoresDeFiltro(d, eje)
    if (!valores || !valores.includes(valor)) {
      throw new ApiError('valor_no_declarado', `'${valor}' no es un valor de '${eje}'`, 422)
    }
  }
  let filas = OBJETOS.filter(o =>
    Object.entries(filtros ?? {}).every(([k, v]) => o[k] === v),
  )
  if (q) {
    const t = q.toLowerCase()
    filas = filas.filter(o => d.listado.busqueda.some(c => String(o[c] ?? '').toLowerCase().includes(t)))
  }
  const total = filas.length
  const desde = (pagina - 1) * tam
  return { mecanismo: codigo, objetos: filas.slice(desde, desde + tam), pagina, tam, total }
}

export function getObjetoMock(codigo, clave) {
  esquemaDe(codigo)
  const objeto = OBJETOS.find(o => o.codigo === clave)
  if (!objeto) throw new ApiError('objeto_no_encontrado', 'objeto no encontrado', 404)
  return { mecanismo: codigo, objeto, metricas: [{ metrica_id: 'n_fichas', valor: OBJETOS.length, etiqueta: 'Fichas' }] }
}

export function getVersionesMock(codigo, clave) {
  const d = esquemaDe(codigo)
  return { mecanismo: codigo, clave, modelo: d.versionado.modelo, versiones: VERSIONES[clave] ?? [] }
}

export function getRegistrosMock(codigo, clave, registro) {
  const d = esquemaDe(codigo)
  const declarado = d.registros_ligados.find(r => r.id === registro)
  if (!declarado) {
    throw new ApiError('registro_no_declarado', `registro ligado '${registro}' no declarado`, 404)
  }
  return { mecanismo: codigo, clave, registro, columnas: declarado.columnas, filas: AUDITORIA[clave] ?? [] }
}

let secuencia = 0

// GEN-76·346 (D-1 de Werner) — el enlace compartible necesita poder RELEER una
// acción ya propuesta (el segundo Owner la abre en su propia pestaña, después
// de que la primera ya la propuso). El mock, hasta hoy, no guardaba nada de lo
// que devolvía `proponerAccionMock` — no hacía falta, porque nada la releía.
// Un mapa en memoria, por `action_id`, es lo mínimo que hace falta para que
// `getAccionMock` pueda existir sin fingir persistencia que no hay.
const _pendientes = new Map()

export function proponerAccionMock(codigo, accionId, params = {}) {
  const d = esquemaDe(codigo)
  const accion = d.acciones.find(a => a.id === accionId)
  if (!accion) {
    throw new ApiError('accion_no_declarada', 'acción no declarada por este mecanismo', 422)
  }
  const motivo = (params.motivo ?? '').trim()
  // El estado del objeto se resuelve una vez y se usa DOS: para la lista blanca
  // (`editable_en`) y para la puerta de D-11. Es el mismo reparto que en el kernel:
  // una regla en un sitio, invocada donde haga falta.
  let estadoObjeto = null

  if (accion.tipo === 'transicion') {
    const t = d.ciclo_de_vida.transiciones.find(x => `${x.de}->${x.a}` === accion.transicion)
    if (t?.requiere_motivo && !motivo) {
      throw new ApiError('parametros_invalidos', 'esta transición exige motivo', 422)
    }
  } else if (accion.tipo === 'registro') {
    const registro = d.registros_ligados.find(r => r.id === accion.registro_ligado)
    const desconocidas = Object.keys(params.valores ?? {}).filter(k => !registro.columnas.includes(k))
    if (desconocidas.length) {
      throw new ApiError('campo_no_declarado', `columnas no declaradas: ${desconocidas.join(', ')}`, 422)
    }
  } else {
    const editables = d.campos.filter(c => !c.solo_lectura).map(c => c.id)
    const desconocidas = Object.keys(params.valores ?? {}).filter(k => !editables.includes(k))
    if (desconocidas.length) {
      throw new ApiError('campo_no_declarado', `campos no declarados o de solo lectura: ${desconocidas.join(', ')}`, 422)
    }
    // La lista blanca del descriptor, aplicada ENTERA: `solo_lectura` es
    // absoluto y `editable_en` depende del estado. En un alta, el estado es el
    // inicial; en una edición, el del objeto — y si no se puede leer, se rechaza
    // (fail-closed), nunca «por si acaso».
    estadoObjeto = d.ciclo_de_vida.inicial
    if (accion.tipo !== 'alta') {
      const objeto = OBJETOS.find(o => o.codigo === params.clave)
      if (!objeto) throw new ApiError('objeto_no_encontrado', 'objeto no encontrado', 404)
      estadoObjeto = objeto.estado
    }
    // R-13 — una edición que no cambia nada se rechaza al proponer: con la puerta de
    // D-11 encima le pediría a un humano aprobar un cambio que no cambia nada, y un
    // aprobador al que se le presenta un diff vacío aprende que los diffs no hay que
    // leerlos.
    //
    // ⚠️ EL ORDEN IMPORTA Y ESTÁ MEDIDO CONTRA EL KERNEL, no razonado: un objeto
    // que no existe responde ANTES (404 `objeto_no_encontrado`), porque en el kernel
    // la lectura del estado vive en `_estado_actual` y esta comprobación en
    // `_params_para`, que corre después. Puesta antes de la lectura, el mock
    // respondía 422 donde el kernel responde 404 — paridad de comportamiento rota
    // en el sitio exacto donde este expediente ya se ha quemado dos veces.
    if (accion.tipo === 'edicion' && !Object.keys(params.valores ?? {}).length) {
      throw new ApiError('parametros_invalidos', 'una edición debe cambiar al menos un campo', 422)
    }
    for (const id of Object.keys(params.valores ?? {})) {
      const campo = d.campos.find(c => c.id === id)
      // V-5b — «DECLARADO», no «no vacío»: el centinela de «sin restricción» es
      // la ausencia de la clave. Con el criterio anterior, `editable_en: []`
      // —que se lee «en ningún estado»— concedía en TODOS.
      if (campo?.editable_en != null && !campo.editable_en.includes(estadoObjeto)) {
        throw new ApiError(
          'campo_no_editable_en_estado',
          `el campo '${id}' no es editable con el objeto en «${estadoObjeto}»: solo en ${campo.editable_en.join(', ')}`,
          422,
        )
      }
    }
    if (accion.tipo === 'alta') {
      const faltan = d.campos
        .filter(c => c.requerido && !c.solo_lectura)
        .filter(c => !String((params.valores ?? {})[c.id] ?? '').trim())
        .map(c => c.id)
      if (faltan.length) {
        throw new ApiError('parametros_invalidos', `faltan campos requeridos: ${faltan.join(', ')}`, 422)
      }
    }
  }

  const t = accion.tipo === 'transicion'
    ? d.ciclo_de_vida.transiciones.find(x => `${x.de}->${x.a}` === accion.transicion)
    : null
  secuencia += 1
  const preview = {
    action_id: `mec-act-${secuencia}`,
    chat_id: 'mecanismos',
    action_type: accion.accion_kernel,
    estado: 'pendiente_aprobacion',
    resumen: `${accion.etiqueta} en ${codigo}. Motivo: ${motivo}`,
    diff: { antes: null, despues: params.valores ?? { estado: accion.transicion?.split('->')[1] } },
    reversibilidad: accion.accion_kernel === 'mec_proponer_objeto' ? 'reversible' : 'irreversible',
    resultado: null,
    escalada: null,
    requiere_aprobacion: true,
    // Paridad de COMPORTAMIENTO, no solo de documento: hasta D-11 esto se calculaba
    // SOLO desde la transición, así que el mock decía «la aprueba el proponente»
    // sobre una edición que el kernel ya no le deja aprobar. Un mock que promete un
    // camino que el original cierra no es una prueba: es una coartada — la lección
    // de #493/#494 aplicada a la puerta en vez de a un código de error.
    //
    // La regla es la del kernel (`exige_puerta_de_owner`): edición + puerta
    // declarada + objeto EN VIGOR. Depende del ESTADO, no del tipo de la acción,
    // así que la misma edición sobre un borrador no exige segunda persona.
    aprobador_distinto:
      accion.tipo === 'transicion'
        ? Boolean(t?.requiere_aprobacion_owner)
        : accion.tipo === 'edicion' &&
          Boolean(accion.requiere_aprobacion_owner) &&
          estadoObjeto === d.ciclo_de_vida.vigente,
  }
  _pendientes.set(preview.action_id, preview)
  return preview
}

/**
 * GET /v1/actions/{action_id} (GEN-76·346, D-1 de Werner) — el enlace
 * compartible: espejo del `GET` de solo lectura de `acciones_api.py`. Sin
 * autorización que replicar aquí (el mock no tiene usuarios de verdad ni
 * `ACCESS_MATRIX`): se limita a devolver lo que `proponerAccionMock` ya
 * guardó, o el mismo 404 que el kernel si el id no existe — la paridad que
 * importa para probar el front es la FORMA de la respuesta, no la puerta.
 */
export function getAccionMock(actionId) {
  const preview = _pendientes.get(actionId)
  if (!preview) {
    throw new ApiError('accion_no_encontrada', 'accion no encontrada', 404)
  }
  return preview
}
